import { useMemo, useState } from 'react'
import type { Category } from '@mc/api'
import { useTranslation } from '@mc/i18n'
import {
  AI_MOTIVATION_KEY,
  useCategoriesStatic,
  useCategoryNameToIdMap,
  useMotivations,
  useMotivationToCategoriesMap,
  useMotivationsCopyMap,
  useSelectedCategories,
  useOtherCategories,
} from 'pages/find-my-classes/hooks/useCategorySelections'
import { ctaPath } from '../../ctaPath'
import { Sel } from './HomepageQuiz'

export const useHomepageQuiz = (preselectedCategories: Category[] = []) => {
  const { t } = useTranslation('@mc/next')
  const categories = useCategoriesStatic()

  const { selectedCategories, toggle, isSelected } = useSelectedCategories(
    preselectedCategories.map((cat) => cat.id) || [],
  )
  const selectedCategoryIds = useMemo(
    () => [...selectedCategories.values()],
    [selectedCategories],
  )

  const otherCategories = useOtherCategories()
  const otherIsPreselected = useMemo(
    () =>
      preselectedCategories.some((cat) =>
        otherCategories.includes(
          cat.name as 'Games & Digital Media' | 'Outdoor Adventures & Events',
        ),
      ),
    [otherCategories, preselectedCategories],
  )
  const catNameToId = useCategoryNameToIdMap()
  const motivationToCatName = useMotivationToCategoriesMap(otherIsPreselected)
  const motivations = useMotivations(otherIsPreselected)
  const motivationCopyMap = useMotivationsCopyMap(motivations, t)
  const otherCategoryIds =
    motivationToCatName.other?.map((otherCatName) =>
      catNameToId.get(otherCatName),
    ) || []

  const trackedCategoryIds = selectedCategoryIds.filter(
    (categoryId) => !otherCategoryIds.includes(categoryId),
  )

  const trackedCategoryNames = trackedCategoryIds.map(
    (categoryId) =>
      categories?.find((c) => Number(c.id) === categoryId)?.name ??
      '<null category>', // null should never happen
  )

  const initialMotivations = useMemo(() => {
    const initial = new Map<string, boolean>()

    if (preselectedCategories.length) {
      const preselectedCategoryNames = new Set(
        preselectedCategories.map((cat) => cat.name),
      )
      Object.entries(motivationToCatName).forEach(
        ([motivation, categoryNames]) => {
          const hasPreselectedCategory = categoryNames?.some((name) =>
            preselectedCategoryNames.has(name),
          )
          if (hasPreselectedCategory) {
            initial.set(motivation, true)
            categoryNames
              ?.filter((name) => !preselectedCategoryNames.has(name))
              .forEach((name) => {
                toggle(catNameToId.get(name) as number)
              })
          }
        },
      )
    }
    return initial
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedCategories.map((cat) => cat.id).join()])

  const [sel, setSel] = useState<Sel>(initialMotivations)

  const ctaHref = ctaPath({
    skipQ1: true,
    categories: selectedCategoryIds,
    ai: !!sel.get(AI_MOTIVATION_KEY),
  })

  const selectedOptions = [...sel.entries()]
    .filter(([_key, val]) => !!val)
    .map(([motivation]) => motivationCopyMap[motivation])

  return {
    sel,
    setSel,
    toggle,
    isSelected,
    selectedOptions,
    ctaHref,
    trackedCategoryNames,
    trackedCategoryIds,
    otherIsPreselected,
  }
}
